import {
  type ConfigurationOperation,
  isAnnulOperation,
  isEnumLiteralCreateOperation,
  isLargeOperation,
} from '@festina/configuration-general-types/configuration'
import type { BranchKey } from '@festina-life/api'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'

import { enumLiteralPath } from '../../../../system/paths'
import { createOperationName } from '../../../../uiModels/annulOperations/helpers'
import { getConfigurationItemType } from '../../../../uiModels/api/operations/helpers'
import type { OperationWithSourceInfo } from '../../../../uiModels/types'
import { LocationAwareOperationLink } from '../../../configurationOperationLink/LocationAwareOperationLink'

type Props = {
  conflicts: OperationWithSourceInfo<ConfigurationOperation>[]
  baseBranchKey: BranchKey
  conflictsWorkspaceName: string
}

export const RebaseBranchConflicts = ({
  baseBranchKey,
  conflicts,
  conflictsWorkspaceName,
}: Props) => {
  const translate = useTranslate()

  return conflicts.length ? (
    <Stack className="flex-col">
      <Alert
        severity="warning"
        title={translate('branchRebaseForm.conflictsRebaseReview.description', {
          baseBranchName: baseBranchKey.name,
          conflictsWorkspaceName,
        })}
      />
      <Stack className="flex-col max-h-[50vh] overflow-y-auto overscroll-contain">
        {conflicts.map((configurationOperation) => {
          const operation = configurationOperation.internalOperation
          const operationKey = isLargeOperation(operation)
            ? operation.innerOperationKey
            : operation.operationKey
          const itemName = isAnnulOperation(operation)
            ? (operation.name ?? '')
            : createOperationName(operation)
          const isEnumLiteral = isEnumLiteralCreateOperation(operation)
          const [enumName, literalName] = itemName.split('.')
          let itemId = itemName
          if (isAnnulOperation(operation)) {
            itemId = operationKey ?? ''
          } else if (isEnumLiteral) {
            itemId = operation.entity?.enumName ?? ''
          }

          return (
            <LocationAwareOperationLink
              key={operationKey ?? ''}
              layer={configurationOperation.layer}
              block={configurationOperation.block}
              itemId={itemId}
              itemLabel={
                isAnnulOperation(operation) || isEnumLiteral
                  ? itemName
                  : undefined
              }
              propertyPath={
                isEnumLiteral
                  ? enumLiteralPath({ enumName, literalName })
                  : undefined
              }
              type={getConfigurationItemType(operation)}
              branchKey={baseBranchKey}
              workspaceKey={undefined}
              operationKey={operationKey}
              isDeleteEnabled={false}
              isDraft={configurationOperation.isDraft}
            />
          )
        })}
      </Stack>
    </Stack>
  ) : null
}
